import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { appsDb, linksDb, notesDb, projectsDb, questionsDb, tipsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { LinkRow } from '../../components/rows/LinkRow.js';
import { QARow } from '../../components/rows/QARow.js';
import { NoteRow } from '../../components/rows/NoteRow.js';
import { AppRow } from '../../components/rows/AppRow.js';
import { TipRow } from '../../components/rows/TipRow.js';
import { Spinner } from '../../components/shared/Spinner.js';
import { EditOwnButton } from '../../components/me/EditOwnButton.js';
import { DeleteOwnButton } from '../../components/me/DeleteOwnButton.js';
import { ForbiddenPage } from '../ForbiddenPage.js';

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { profile } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const projectQ = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsDb.findById(id),
    enabled: !!id,
  });
  const linksQ = useQuery({
    queryKey: ['project', id, 'links'],
    queryFn: () => linksDb.findShared({ count: 200 }),
    enabled: !!id,
  });
  const questionsQ = useQuery({
    queryKey: ['project', id, 'questions'],
    queryFn: () => questionsDb.findShared({ count: 200 }),
    enabled: !!id,
  });
  const notesQ = useQuery({
    queryKey: ['project', id, 'notes'],
    queryFn: () => notesDb.findShared({ count: 200 }),
    enabled: !!id,
  });
  const appsQ = useQuery({
    queryKey: ['project', id, 'apps'],
    queryFn: () => appsDb.findShared({ count: 200 }),
    enabled: !!id,
  });
  const tipsQ = useQuery({
    queryKey: ['project', id, 'tips'],
    queryFn: () => tipsDb.findShared({ count: 200 }),
    enabled: !!id,
  });

  if (projectQ.isPending) {
    return (
      <div className="page">
        <Spinner />
      </div>
    );
  }
  if (projectQ.error || !projectQ.data) return <ForbiddenPage />;
  const p = projectQ.data;
  const isOwner = profile?.id === p.createdBy;

  const projectLinks = (linksQ.data ?? []).filter((x) => x.projectId === id);
  const projectQs = (questionsQ.data ?? []).filter((x) => x.projectId === id);
  const projectNotes = (notesQ.data ?? []).filter((x) => x.projectId === id);
  const projectApps = (appsQ.data ?? []).filter((x) => x.projectId === id);
  const projectTips = (tipsQ.data ?? []).filter((x) => x.projectId === id);

  const refCount =
    projectLinks.length +
    projectQs.length +
    projectNotes.length +
    projectApps.length +
    projectTips.length;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center', gap: 12 }}>
        <RouterLink to="/projects" className="btn ghost sm">
          ← トピック一覧
        </RouterLink>
        {isOwner && (
          <div style={{ display: 'flex', gap: 6 }}>
            <EditOwnButton kind="project" id={p.id} size="sm" />
            <DeleteOwnButton
              kind="トピック"
              title={p.name}
              onDelete={async () => {
                await projectsDb.remove(p.id);
              }}
              invalidateKeys={[
                ['projects', 'shared'],
                ['projects', 'available'],
                ['projects', 'mine'],
                ['project', p.id],
              ]}
              extraDescription={
                refCount > 0
                  ? `あなたから見える範囲で ${refCount} 件の投稿がこのトピックを参照しています（URL: ${projectLinks.length} / 質問: ${projectQs.length} / メモ: ${projectNotes.length} / アプリ: ${projectApps.length} / TIPS: ${projectTips.length}）。\n他者が紐付けた private 投稿は集計に含まれません。削除しても投稿自体は残り、紐付けが「(削除済トピック)」表示になります。`
                  : 'あなたから見える参照投稿はありません（他者の private 投稿は集計に含まれません）。削除しても投稿自体は残ります。'
              }
              onAfterDelete={() => {
                qc.invalidateQueries({ queryKey: ['projects'] });
                nav('/projects');
              }}
            />
          </div>
        )}
      </div>

      <div className="page-header">
        <div>
          <div className="page-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, background: p.color, borderRadius: 2 }} />
            PROJECT
            {p.visibility === 'private' && (
              <span className="vis-badge" data-vis="private" style={{ marginLeft: 6 }}>
                非公開
              </span>
            )}
          </div>
          <h1 className="page-title">{p.name}</h1>
          <div className="page-sub">{p.description}</div>
        </div>
        <div className="page-header-actions">
          <span className="badge" data-status={p.status}>
            <span className="badge-dot" />
            {p.status}
          </span>
        </div>
      </div>

      {projectApps.length > 0 && (
        <>
          <div className="section-title">
            このトピックのアプリ <span className="section-count">({projectApps.length})</span>
          </div>
          <div style={{ borderTop: '1px solid var(--line)', marginBottom: 30 }}>
            {projectApps.map((a) => <AppRow key={a.id} app={a} />)}
          </div>
        </>
      )}

      {projectLinks.length > 0 && (
        <>
          <div className="section-title">
            関連URL <span className="section-count">({projectLinks.length})</span>
          </div>
          <div style={{ borderTop: '1px solid var(--line)', marginBottom: 30 }}>
            {projectLinks.map((l) => <LinkRow key={l.id} link={l} />)}
          </div>
        </>
      )}

      {projectNotes.length > 0 && (
        <>
          <div className="section-title">
            検証メモ <span className="section-count">({projectNotes.length})</span>
          </div>
          <div style={{ borderTop: '1px solid var(--line)', marginBottom: 30 }}>
            {projectNotes.map((n) => <NoteRow key={n.id} note={n} />)}
          </div>
        </>
      )}

      {projectTips.length > 0 && (
        <>
          <div className="section-title">
            このトピックの TIPS <span className="section-count">({projectTips.length})</span>
          </div>
          <div style={{ borderTop: '1px solid var(--line)', marginBottom: 30 }}>
            {projectTips.map((t) => <TipRow key={t.id} tip={t} />)}
          </div>
        </>
      )}

      {projectQs.length > 0 && (
        <>
          <div className="section-title">
            関連する質問 <span className="section-count">({projectQs.length})</span>
          </div>
          <div style={{ borderTop: '1px solid var(--line)' }}>
            {projectQs.map((q) => <QARow key={q.id} q={q} />)}
          </div>
        </>
      )}

      {projectApps.length === 0 &&
        projectLinks.length === 0 &&
        projectNotes.length === 0 &&
        projectQs.length === 0 &&
        projectTips.length === 0 && (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: 'var(--ink-3)',
              border: '1px dashed var(--line)',
              borderRadius: 6,
              marginTop: 14,
            }}
          >
            このトピックに紐づく投稿はまだありません。
          </div>
        )}
    </div>
  );
}
